import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import styles from "./home.module.css";
import { useSelector } from "react-redux";
import { State } from "../state";
import Seo from "./Seo";
import Testimonials from "./testimonials/Testimonials";
import { landingPages } from "../content/landingPages";
import { fieldProven } from "../content/testimonials";

interface FaqItem {
  question?: string;
  answer?: string;
  linkText?: string;
  linkTo?: string;
}

// Generic labels for the hero proof strip. Kept local (mirroring the
// Testimonials component's own UI map) so the station-specific proof copy
// stays in content/testimonials.ts and this stays out of the big languageData
// surface. Both languages are always present (no fallback strings).
const PROOF_UI: Record<"tr" | "en", { badge: string; maps: string }> = {
  tr: { badge: "Sahada kanıtlandı", maps: "Google Haritalar'da gör" },
  en: { badge: "Proven in the field", maps: "See on Google Maps" },
};

// One emoji per solution (landing page), keyed by Turkish slug, for the
// solutions grid under the fraud band. Falls back to a wrench for any
// unmapped slug so a newly added landing page never renders blank.
const solutionIcons: Record<string, string> = {
  "akaryakit-istasyonu-yazilimi": "⛽",
  "benzin-istasyonu-yazilimi": "🏪",
  "vardiya-alma-programi": "🕒",
  "akaryakit-istasyonu-muhasebe-programi": "🧾",
  "nakit-yatirma-dogrulama": "🏦",
  "kredi-karti-odeme-dogrulama": "💳",
  "canli-satis-takibi": "📈",
  "tank-stok-takibi": "🛢️",
  "akaryakit-siparis-onerisi": "📦",
};

// The "benzin istasyonu yazilimi" landing page is a near-synonym SEO variant of
// "akaryakit istasyonu yazilimi" (kept as separate pages to avoid keyword
// cannibalization, but the same product to a visitor). Show only one in the
// home solutions grid; both pages stay live and linked from the nav + sitemap.
const HOME_SOLUTIONS_HIDE = new Set(["benzin-istasyonu-yazilimi"]);

function HomePage(): JSX.Element {
  const { data: languageData, appLanguage } = useSelector((state: State) => state.languageReducer);
  const { isLoggedIn, user } = useSelector((state: State) => state.authReducer);

  const homeText = languageData?.home ?? {};
  const langPrefix = appLanguage === "en" ? "/en" : "";
  const solutionLang: "tr" | "en" = appLanguage === "en" ? "en" : "tr";
  const fraudHref = `${langPrefix}/fraud-prevention/`;
  const reconciliationBadges = languageData?.reconciliationV2?.badges ?? {};

  const roles = user?.roles ?? [];
  const isAdmin = roles.includes("ROLE_ADMIN");
  const canUseCorrection = isAdmin || roles.includes("ROLE_USER");

  const shortcuts: Array<{
    title?: string;
    description?: string;
    button?: string;
    to: string;
    tone: "primary" | "secondary" | "neutral";
  }> = [];

  if (isAdmin && homeText.shortcuts?.reconciliation) {
    shortcuts.push({
      ...homeText.shortcuts.reconciliation,
      to: "/admin/reconciliation/v2/workspace",
      tone: "primary",
    });
  }

  if (canUseCorrection && homeText.shortcuts?.correction) {
    shortcuts.push({
      ...homeText.shortcuts.correction,
      to: "/admin/payment-type-correction",
      tone: "secondary",
    });
  }

  const shortcutToneClassMap = {
    primary: styles.shortcutTonePrimary,
    secondary: styles.shortcutToneSecondary,
    neutral: styles.shortcutToneNeutral,
  };

  const faqItems: FaqItem[] = homeText.faq?.items ?? [];
  const faqSchema =
    faqItems.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }
      : null;

  const featureIcons = ["🔄", "💳", "🧾", "🛡️", "🧠", "📊", "🔍", "📈", "🛢️", "📦"];
  const exampleToneClasses = [
    styles.exampleToneBlue,
    styles.exampleToneOrange,
    styles.exampleToneGreen,
  ];

  const scrollToId = (id: string) => (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    const target = document.getElementById(id);
    if (!target) {
      return;
    }
    event.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToSolutions = scrollToId("solutions");
  const scrollToHowItWorks = scrollToId("how-it-works");

  return (
    <div className={styles.page}>
      <Seo path="/" />
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.eyebrow}>{homeText.hero?.eyebrow}</div>

          <div className={styles.badgeRow}>
            {(homeText.hero?.badges ?? []).map((badge: string, index: number) => (
              <span key={index} className={styles.heroBadge}>
                {badge}
              </span>
            ))}
          </div>

          <h1 className={styles.heroTitle}>{homeText.hero?.title}</h1>

          <p className={styles.heroSubtitle}>{homeText.hero?.subtitle}</p>

          <div className={styles.heroActions}>
            <NavLink to="/contact/" className={styles.primaryButton}>
              {homeText.hero?.primaryCta}
            </NavLink>
            <a
              href="#solutions"
              className={styles.secondaryButton}
              onClick={scrollToSolutions}
            >
              {homeText.hero?.secondaryCta}
              <span className={styles.secondaryButtonArrow} aria-hidden="true">
                ↓
              </span>
            </a>
          </div>

          {homeText.hero?.howItWorks && (
            <a
              href="#how-it-works"
              className={styles.heroHowLink}
              onClick={scrollToHowItWorks}
            >
              {homeText.hero.howItWorks}
              <span aria-hidden="true"> →</span>
            </a>
          )}

          <div className={styles.trustStrip}>
            {(homeText.hero?.trust ?? []).map(
              (
                item: { value?: string; label?: string },
                index: number
              ) => (
                <div key={index} className={styles.trustCard}>
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              )
            )}
          </div>
        </div>

        <div className={styles.heroVisual}>
          <div className={styles.orbOne}></div>
          <div className={styles.orbTwo}></div>

          <div className={styles.mockWindow}>
            <div className={styles.mockToolbar}>
              <div className={styles.mockDots}>
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className={styles.mockWindowTitle}>
                {homeText.hero?.visual?.windowTitle}
              </span>
            </div>

            <div className={styles.mockBody}>
              <div className={styles.mockColumns}>
                <div className={styles.mockPanel}>
                  <div className={styles.mockPanelTitle}>
                    {homeText.hero?.visual?.leftTitle}
                  </div>

                  <div className={styles.mockRow}>
                    <div className={styles.mockMeta}>
                      <strong>14:23</strong>
                      <span>1,000.00</span>
                    </div>
                    <span className={`${styles.mockPill} ${styles.pillMatched}`}>
                      {reconciliationBadges?.matched || "MATCHED"}
                    </span>
                  </div>

                  <div className={styles.mockRow}>
                    <div className={styles.mockMeta}>
                      <strong>14:31</strong>
                      <span>650.00</span>
                    </div>
                    <span className={`${styles.mockPill} ${styles.pillPending}`}>
                      {reconciliationBadges?.pending || "PENDING"}
                    </span>
                  </div>

                  <div className={styles.mockRow}>
                    <div className={styles.mockMeta}>
                      <strong>14:42</strong>
                      <span>420.00</span>
                    </div>
                    <span className={`${styles.mockPill} ${styles.pillFraud}`}>
                      {reconciliationBadges?.fraudRisk || "FRAUD RISK"}
                    </span>
                  </div>
                </div>

                <div className={styles.mockPanel}>
                  <div className={styles.mockPanelTitle}>
                    {homeText.hero?.visual?.rightTitle}
                  </div>

                  <div className={styles.mockRow}>
                    <div className={styles.mockMeta}>
                      <strong>14:15</strong>
                      <span>1,000.00</span>
                    </div>
                    <span className={`${styles.mockPill} ${styles.pillSelectable}`}>
                      {reconciliationBadges?.selectable || "SELECTABLE"}
                    </span>
                  </div>

                  <div className={styles.mockRow}>
                    <div className={styles.mockMeta}>
                      <strong>14:29</strong>
                      <span>650.00</span>
                    </div>
                    <span className={`${styles.mockPill} ${styles.pillLinked}`}>
                      {reconciliationBadges?.linked || "LINKED"}
                    </span>
                  </div>

                  <div className={styles.mockRow}>
                    <div className={styles.mockMeta}>
                      <strong>14:40</strong>
                      <span>420.00</span>
                    </div>
                    <span className={`${styles.mockPill} ${styles.pillOtherShift}`}>
                      {reconciliationBadges?.matchedOtherShift || "OTHER SHIFT"}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.mockInsights}>
                <div className={styles.mockInsightCard}>
                  <div className={styles.mockInsightTitle}>
                    {homeText.hero?.visual?.correctionTitle}
                  </div>
                  <p>{homeText.hero?.visual?.correctionText}</p>
                </div>

                <div className={styles.mockInsightCard}>
                  <div className={styles.mockInsightTitle}>
                    {homeText.hero?.visual?.auditTitle}
                  </div>
                  <p>{homeText.hero?.visual?.auditText}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.proofStrip} aria-label={PROOF_UI[solutionLang].badge}>
        <span className={styles.proofBadge} lang={solutionLang}>
          <span className={styles.proofDot} aria-hidden="true"></span>
          {PROOF_UI[solutionLang].badge}
        </span>
        <p className={styles.proofText}>{fieldProven.headline[solutionLang]}</p>
        <div className={styles.proofLinks}>
          <a
            href={fieldProven.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.proofLink}
          >
            {fieldProven.station} ↗
          </a>
          <a
            href={fieldProven.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.proofLink}
          >
            {PROOF_UI[solutionLang].maps} ↗
          </a>
        </div>
      </section>

      {homeText.fraudCallout && (
        <section className={styles.fraudCallout}>
          <div className={styles.fraudCalloutShell}>
            <div className={styles.fraudCalloutText}>
              <span className={styles.fraudCalloutEyebrow}>
                {homeText.fraudCallout.eyebrow}
              </span>
              <h2>{homeText.fraudCallout.title}</h2>
              <p>{homeText.fraudCallout.text}</p>
            </div>
            <div className={styles.fraudCalloutAside}>
              {homeText.fraudCallout.stat && (
                <span className={styles.fraudCalloutStat}>
                  {homeText.fraudCallout.stat}
                </span>
              )}
              <NavLink to={fraudHref} className={styles.fraudCalloutButton}>
                {homeText.fraudCallout.button}
              </NavLink>
            </div>
          </div>
        </section>
      )}

      {homeText.solutions && (
        <section id="solutions" className={styles.solutionsSection}>
          <div className={styles.sectionHeader}>
            <h2>{homeText.solutions.title}</h2>
            <p>{homeText.solutions.subtitle}</p>
          </div>
          <div className={styles.solutionsGrid}>
            {landingPages
              .filter((p) => !HOME_SOLUTIONS_HIDE.has(p.slug))
              .map((p) => (
              <NavLink
                key={p.slug}
                to={
                  solutionLang === "en"
                    ? `/en/${p.enSlug}/`
                    : `/${p.slug}/`
                }
                className={styles.solutionCard}
              >
                <span className={styles.solutionIcon} aria-hidden="true">
                  {solutionIcons[p.slug] ?? "🔧"}
                </span>
                <span className={styles.solutionName}>
                  {p[solutionLang].serviceName}
                </span>
                <span className={styles.solutionArrow} aria-hidden="true">
                  →
                </span>
              </NavLink>
            ))}
          </div>
        </section>
      )}

      {isLoggedIn && shortcuts.length > 0 && (
        <section className={styles.shortcutsSection}>
          <div className={styles.sectionHeader}>
            <h2>{homeText.shortcuts?.title}</h2>
            <p>{homeText.shortcuts?.subtitle}</p>
          </div>

          <div className={styles.shortcutsGrid}>
            {shortcuts.map((item, index) => (
              <div
                key={index}
                className={`${styles.shortcutCard} ${
                  shortcutToneClassMap[item.tone]
                }`}
              >
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <NavLink to={item.to} className={styles.shortcutButton}>
                  {item.button}
                </NavLink>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className={styles.featuresSection}>
        <div className={styles.sectionHeader}>
          <h2>{homeText.features?.title}</h2>
          <p>{homeText.features?.subtitle}</p>
        </div>

        <div className={styles.featuresGrid}>
          {(homeText.features?.items ?? []).map(
            (item: { title?: string; description?: string }, index: number) => (
              <div key={index} className={styles.featureCard}>
                <div className={styles.featureIcon}>
                  {featureIcons[index % featureIcons.length]}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            )
          )}
        </div>
      </section>

      <section id="how-it-works" className={styles.workflowSection}>
        <div className={styles.sectionHeader}>
          <h2>{homeText.workflow?.title}</h2>
          <p>{homeText.workflow?.subtitle}</p>
        </div>

        <div className={styles.stepGrid}>
          {(homeText.workflow?.steps ?? []).map(
            (
              step: { title?: string; description?: string },
              index: number
            ) => (
              <div key={index} className={styles.stepCard}>
                <div className={styles.stepNumber}>{index + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            )
          )}
        </div>
      </section>

      <section className={styles.examplesSection}>
        <div className={styles.sectionHeader}>
          <h2>{homeText.examples?.title}</h2>
          <p>{homeText.examples?.subtitle}</p>
        </div>

        <div className={styles.examplesGrid}>
          {(homeText.examples?.items ?? []).map(
            (
              item: { title?: string; description?: string; outcome?: string },
              index: number
            ) => (
              <div
                key={index}
                className={`${styles.exampleCard} ${
                  exampleToneClasses[index % exampleToneClasses.length]
                }`}
              >
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <span className={styles.exampleOutcome}>{item.outcome}</span>
              </div>
            )
          )}
        </div>
      </section>

      {faqItems.length > 0 && (
        <section className={styles.faqSection}>
          {faqSchema && (
            <Helmet defer={false}>
              <script type="application/ld+json">
                {JSON.stringify(faqSchema)}
              </script>
            </Helmet>
          )}
          <div className={styles.sectionHeader}>
            <h2>{homeText.faq?.title}</h2>
            <p>{homeText.faq?.subtitle}</p>
          </div>

          <div className={styles.faqList}>
            {faqItems.map((item, index) => (
              <details key={index} className={styles.faqItem}>
                <summary className={styles.faqQuestion}>
                  {item.question}
                </summary>
                <p className={styles.faqAnswer}>{item.answer}</p>
                {item.linkTo && (
                  <NavLink to={item.linkTo} className={styles.faqLink}>
                    {item.linkText} →
                  </NavLink>
                )}
              </details>
            ))}
          </div>
        </section>
      )}

      <Testimonials lang={appLanguage === "tr" ? "tr" : "en"} />

      <section className={styles.ctaSection}>
        <div className={styles.ctaShell}>
          <h2>{homeText.cta?.title}</h2>
          <p>{homeText.cta?.subtitle}</p>
          <div className={styles.ctaActions}>
            <NavLink to="/contact/" className={styles.ctaPrimary}>
              {homeText.cta?.primary}
            </NavLink>
            <NavLink to={`${langPrefix}/blog/`} className={styles.ctaSecondary}>
              {homeText.cta?.secondary}
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;